import React, { KeyboardEvent, MouseEvent, useRef, useState } from 'react';
import { generateClassNameByBehaviorSettings } from '../../../../utils/behaviorSettings';
import DefaultPicture from '../../../01_atoms/DefaultPicture';
import { LinkWithLabel, DefaultPictureType } from '../../../../types';
import { LinkChevron } from '../../../02_molecules/Link';
import FormattedText from '../../../01_atoms/FormattedText';
import CloseIcon from '../../../../public/static/icons/close-rounded.svg';
import { BehaviourSettingsType } from '../../../../utils/behaviorSettings/prop-types';

import s from './index.module.scss';

type ItemProps = {
  name: string;
  role?: string;
  popupLinkText?: string;
  description?: string;
  image?: DefaultPictureType;
  link: LinkWithLabel;
};

type Props = {
  items: ItemProps[];
  behaviorSettings?: BehaviourSettingsType;
  uuid?: string;
};

const BBTiledContent = ({ items, behaviorSettings = {}, uuid = undefined }: Props) => {
  const [popupItem, setPopupItem] = useState<ItemProps | null>(null);

  const ref = useRef<HTMLDivElement | null>(null);

  const openPopup = (e: MouseEvent, item: ItemProps) => {
    e.preventDefault();
    setPopupItem(item);

    // Wait the "next loop" to have "popup" element in DOM.
    setTimeout(() => {
      const el = ref.current?.querySelector(
        '[data-component="bb-tiled-content-popup"]',
      ) as HTMLDivElement;

      if (el) {
        el.focus();
      }
    }, 0);
  };

  const closePopup = () => {
    // Set focus back to displayed item.
    const index = items.findIndex((el) => el === popupItem);

    const el = ref.current?.querySelector(
      `[data-component="bb-tiled-content-item"]:nth-child(${index + 1}) a`,
    ) as HTMLDivElement;

    if (el) {
      el.focus();
    }

    // Close the popup.
    setPopupItem(null);
  };

  const clickPopup = (e: MouseEvent) => {
    const parent = e.target as HTMLDivElement;

    if (parent?.parentElement?.classList.contains('bb')) {
      closePopup();
    }
  };

  const keyUpPopup = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && popupItem !== null) {
      closePopup();
    }
  };

  return (
    <div
      className={`bb ${s['bb-tiled-content']} ${generateClassNameByBehaviorSettings(behaviorSettings)}`}
      id={uuid}
      data-component="tiled-content"
      onKeyDown={(e) => keyUpPopup(e)}
      ref={ref}
    >
      <div className="container">
        <div className={s['bb-tiled-content__wrapper']}>
          <div className={s['bb-tiled-content__items']}>
            {items.map((item, index) => (
              <div
                className={s['bb-tiled-content__item']}
                key={index}
                data-component={'bb-tiled-content-item'}
              >
                <div className={s['bb-tiled-content__item-wrapper']}>
                  <div className={s['bb-tiled-content__item-content']}>
                    <div className={s['bb-tiled-content__item-image']}>
                      <div className={s['bb-tiled-content__image-element']}>
                        {item.image && <DefaultPicture loading="lazy" {...item.image} />}
                        {!item.image && <div className={s['bb-tiled-content__image-empty']}></div>}
                      </div>
                    </div>
                    <div className={s['bb-tiled-content__item-name']}>{item.name}</div>
                    {item.role && (
                      <div className={s['bb-tiled-content__item-role']}>{item.role}</div>
                    )}
                    {item.popupLinkText && (
                      <div className={s['bb-tiled-content__item-popup-link']}>
                        <LinkChevron
                          nextLink={{ url: '#', href: '#', children: '' }}
                          onClick={(e) => openPopup(e, item)}
                          isBold={true}
                          label={item.popupLinkText}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {popupItem && (
        <>
          <div className={s['bb-tiled-content__popup']} onClick={(e) => clickPopup(e)}>
            <div
              className={s['bb-tiled-content__popup-root']}
              tabIndex={0}
              data-component={'bb-tiled-content-popup'}
            >
              <div className={s['bb-tiled-content__popup-actions']}>
                <button className={s['bb-tiled-content__popup-close']} onClick={closePopup}>
                  <CloseIcon aria-label={'Close'} />
                </button>
              </div>
              <div className={s['bb-tiled-content__popup-wrapper']}>
                <div className={s['bb-tiled-content__popup-image']}>
                  <div className={s['bb-tiled-content__image-element']}>
                    {popupItem.image && <DefaultPicture loading="lazy" {...popupItem.image} />}
                    {!popupItem.image && <div className={s['bb-tiled-content__image-empty']}></div>}
                  </div>
                </div>
                <div className={s['bb-tiled-content__popup-content']}>
                  <div className={s['bb-tiled-content__popup-heading']}>
                    <div className={s['bb-tiled-content__popup-name']}>{popupItem.name}</div>
                    {popupItem.role && (
                      <div className={s['bb-tiled-content__popup-role']}>{popupItem.role}</div>
                    )}
                  </div>
                  {popupItem.description && (
                    <div className={s['bb-tiled-content__popup-description']}>
                      <FormattedText text={popupItem.description} />
                    </div>
                  )}
                  <div className={s['bb-tiled-content__popup-link']}>
                    {popupItem.link.nextLink && (
                      <LinkChevron
                        nextLink={popupItem.link.nextLink}
                        isBold={true}
                        label={popupItem.link.label}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BBTiledContent;
